import React, { useState, useEffect, useCallback } from "react";
import { Command } from "cmdk";
import { Search, Loader2, CornerDownLeft, Pin } from "lucide-react";
import { cn } from "@/lib/utils";
import { searchProductVariants } from "@/api/sale-api";
import { ProductVariant, Product } from "@/types/product-type";
import { getStockSnapshot } from "./product-sale-utils";

interface SearchResult {
    product: Product;
    variant: ProductVariant;
    key: string;
}

interface Props {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    onSelect: (variantId: string, productId: string, product: Product, variant: ProductVariant) => void;
    /** Called with all current results — pin them all at once */
    onSelectAll?: (items: {
        variantId: string;
        productId: string;
        sku: string;
        product: Product;
        variant: ProductVariant;
    }[]) => Promise<void>;
}

export function ProductSaleSearchUnpin({ isOpen, onOpenChange, onSelect, onSelectAll }: Props) {
    const [searchQuery, setSearchQuery] = useState("");
    const [results, setResults] = useState<SearchResult[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const [isPinningAll, setIsPinningAll] = useState(false);

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedQuery(searchQuery);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Perform search when debounced query changes
    useEffect(() => {
        const performSearch = async () => {
            if (!debouncedQuery.trim()) {
                setResults([]);
                return;
            }

            setIsLoading(true);
            try {
                const response = await searchProductVariants(debouncedQuery, 50);
                if (response.success && response.data) {
                    // Flatten products and variants into search results
                    const searchResults: SearchResult[] = [];
                    response.data.data.forEach((productWithRelation) => {
                        const product = productWithRelation.product;
                        productWithRelation.productVariants.forEach((variant) => {
                            searchResults.push({
                                product,
                                variant,
                                key: `${product.id}-${variant.id}`,
                            });
                        });
                    });
                    setResults(searchResults);
                } else {
                    setResults([]);
                }
            } catch (error) {
                console.error("Search error:", error);
                setResults([]);
            } finally {
                setIsLoading(false);
            }
        };

        performSearch();
    }, [debouncedQuery]);

    // Keyboard Shortcuts Handler
    useEffect(() => {
        const down = (e: KeyboardEvent) => {
            // Toggle with Cmd+Shift+K or Ctrl+Shift+K
            if (e.key === "k" && (e.metaKey || e.ctrlKey) && e.shiftKey) {
                e.preventDefault();
                onOpenChange(!isOpen);
            }

            // Close with Escape
            if (e.key === "Escape" && isOpen) {
                e.preventDefault();
                onOpenChange(false);
            }
        };

        document.addEventListener("keydown", down);
        return () => document.removeEventListener("keydown", down);
    }, [isOpen, onOpenChange]);

    // Reset search when dialog closes
    useEffect(() => {
        if (!isOpen) {
            setSearchQuery("");
            setResults([]);
            setIsPinningAll(false);
        }
    }, [isOpen]);

    const handlePinAll = useCallback(async () => {
        if (!onSelectAll || results.length === 0 || isPinningAll) return;
        setIsPinningAll(true);
        const items = results.map((r) => ({
            variantId: r.variant.id,
            productId: r.product.id,
            sku: r.variant.sku || "NO-SKU",
            product: r.product,
            variant: r.variant,
        }));
        try {
            await onSelectAll(items);
        } finally {
            setIsPinningAll(false);
            onOpenChange(false);
        }
    }, [onSelectAll, results, isPinningAll, onOpenChange]);

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-[100] flex items-start justify-center bg-slate-900/40 pt-[12vh] backdrop-blur-[2px] animate-in fade-in duration-200 px-4"
            onClick={() => onOpenChange(false)}
        >
            <Command
                // Internal Escape handling to ensure the modal closes
                onKeyDown={(e) => {
                    if (e.key === "Escape") {
                        e.preventDefault();
                        onOpenChange(false);
                    }
                }}
                className="w-full max-w-[700px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl animate-in fade-in zoom-in-95 duration-200"
                onClick={(e) => e.stopPropagation()}
                shouldFilter={false} // We handle filtering via API
            >
                {/* Search Header */}
                <div className="flex items-center border-b border-slate-100 px-4 bg-white">
                    <Search className="mr-3 h-4 w-4 text-emerald-500 shrink-0" />
                    <Command.Input
                        placeholder="Search all products by SKU, code or name..."
                        className="flex h-14 w-full bg-transparent py-3 text-sm outline-none placeholder:text-slate-400 font-medium text-slate-700"
                        autoFocus
                        value={searchQuery}
                        onValueChange={setSearchQuery}
                    />
                    <div className="flex items-center gap-1.5 ml-2">
                        {isLoading && (
                            <Loader2 className="h-4 w-4 text-emerald-500 animate-spin mr-2" />
                        )}
                        <kbd className="hidden rounded bg-slate-100 px-2 py-1 font-mono text-[10px] font-bold text-slate-500 sm:block border border-slate-200">
                            ESC
                        </kbd>
                    </div>
                </div>

                {/* Results List */}
                <Command.List className="max-h-[450px] overflow-y-auto p-2 scrollbar-hide">
                    {!searchQuery.trim() ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <div className="bg-slate-50 p-3 rounded-full mb-3">
                                <Search className="h-6 w-6 text-slate-300" />
                            </div>
                            <p className="text-sm font-medium text-slate-500">Start typing to search</p>
                            <p className="text-xs text-slate-400 mt-1">Search by SKU, product code, or name</p>
                        </div>
                    ) : results.length === 0 && !isLoading ? (
                        <Command.Empty className="flex flex-col items-center justify-center py-12 text-center">
                            <div className="bg-slate-50 p-3 rounded-full mb-3">
                                <Search className="h-6 w-6 text-slate-300" />
                            </div>
                            <p className="text-sm font-medium text-slate-500">No products found</p>
                            <p className="text-xs text-slate-400 mt-1">Try a different search term</p>
                        </Command.Empty>
                    ) : (
                        <Command.Group
                            heading={
                                <div className="flex items-center justify-between px-2 pb-2">
                                    <span className="text-[10px] font-black uppercase tracking-[0.1em] text-emerald-500/70">
                                        Search Results ({results.length})
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] text-slate-400 font-medium hidden sm:block">Use arrows to navigate</span>
                                        {onSelectAll && results.length > 0 && (
                                            <button
                                                type="button"
                                                onClick={(e) => { e.preventDefault(); handlePinAll(); }}
                                                disabled={isPinningAll}
                                                className={cn(
                                                    "inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wide transition-all border",
                                                    isPinningAll
                                                        ? "bg-emerald-50 border-emerald-200 text-emerald-400 cursor-not-allowed"
                                                        : "bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700 active:scale-95 shadow-sm"
                                                )}
                                            >
                                                {isPinningAll ? (
                                                    <><Loader2 className="h-3 w-3 animate-spin" /> Pinning…</>
                                                ) : (
                                                    <><Pin className="h-3 w-3" /> Pin All ({results.length})</>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            }
                        >
                            {results.map((item) => {
                                const { remain, sold, total } = getStockSnapshot(item.variant, item.product);
                                const sku = item.variant.sku || "NO-SKU";

                                return (
                                    <Command.Item
                                        key={item.key}
                                        value={item.key}
                                        keywords={[
                                            sku,
                                            item.variant.name || "",
                                            item.product.name || "",
                                            item.product.code || "",
                                        ]}
                                        onSelect={() => {
                                            onSelect(item.variant.id, item.product.id, item.product, item.variant);
                                        }}
                                        className={cn(
                                            "group flex cursor-pointer items-center justify-between rounded-xl px-3 py-3 text-sm outline-none transition-all mb-1",
                                            "aria-selected:bg-emerald-600 aria-selected:text-white"
                                        )}
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            {/* Product Image */}
                                            {(item.variant.imageUrl || item.product.imageUrl) && (
                                                <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-100 shrink-0 border border-slate-200">
                                                    <img
                                                        src={item.variant.imageUrl || item.product.imageUrl || ""}
                                                        alt={item.variant.name || item.product.name}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                            )}

                                            {/* SKU Tag */}
                                            <div className={cn(
                                                "flex h-9 min-w-40 shrink-0 flex-col items-center justify-center rounded-lg border font-mono transition-colors",
                                                "bg-slate-50 border-slate-200 group-aria-selected:bg-emerald-500 group-aria-selected:border-emerald-400"
                                            )}>
                                                <span className="text-[8px] font-black group-aria-selected:text-emerald-100 text-slate-400 uppercase leading-none mb-0.5">SKU</span>
                                                <span className="text-[11px] font-bold group-aria-selected:text-white text-emerald-700 leading-none truncate w-full text-center px-1">
                                                    {sku}
                                                </span>
                                            </div>

                                            <div className="flex flex-col min-w-0">
                                                <span className="font-bold leading-tight truncate">
                                                    {item.variant.name || item.product.name}
                                                </span>
                                                <span className={cn(
                                                    "text-[11px] mt-0.5 truncate",
                                                    "text-slate-500 group-aria-selected:text-emerald-100"
                                                )}>
                                                    {item.product.name}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Stock Metrics */}
                                        <div className="flex items-center gap-2 shrink-0 ml-4">
                                            <div className={cn(
                                                "flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-black uppercase tracking-tight transition-colors",
                                                "bg-slate-100 text-slate-600 group-aria-selected:bg-white/20 group-aria-selected:text-white"
                                            )}>
                                                <span>Sold</span>
                                                <span className="text-xs">{sold}</span>
                                            </div>

                                            <div className={cn(
                                                "flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-black uppercase tracking-tight transition-colors",
                                                remain === 0 ? "bg-red-50 text-red-600 group-aria-selected:bg-red-500/40" : "bg-emerald-50 text-emerald-700 group-aria-selected:bg-emerald-400 group-aria-selected:text-emerald-950",
                                            )}>
                                                <span>Left</span>
                                                <span className="text-xs">{remain}</span>
                                            </div>

                                            <CornerDownLeft className="h-3 w-3 opacity-0 group-aria-selected:opacity-100 transition-opacity ml-1" />
                                        </div>
                                    </Command.Item>
                                );
                            })}
                        </Command.Group>
                    )}
                </Command.List>

                {/* Footer info */}
                <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50/50 px-4 py-3 text-[10px] text-slate-400 font-medium">
                    <div className="flex gap-4">
                        <span className="flex items-center gap-1"><kbd className="bg-white border border-slate-200 px-1 rounded text-[9px] font-bold">↑↓</kbd> to navigate</span>
                        <span className="flex items-center gap-1"><kbd className="bg-white border border-slate-200 px-1 rounded text-[9px] font-bold">↵</kbd> to select</span>
                    </div>
                    <span>Product Search</span>
                </div>
            </Command>
        </div>
    );
}
